import type { AlwaysAiUser } from "../../../shared/api-types";
import {
  can,
  canReadRdWorkspace,
  isAlwaysAiAdmin,
  isSessionDraftPath,
} from "./permissions";

// The single rail's sections (NO-754). Sections follow permissions, never the
// role name, so a dual-role viewer needs no special case:
//
// - My practice: the viewer's own Appointments (`appointments:read:own` with
//   rd-home on), their in-app Notifications when the `notifications` flag is
//   on (NO-830), Messages under it when `patient-messaging` is on (NO-925), their Patients list when `patient-detail` is on (NO-835, in
//   place of the Soon Patients item), plus the remaining Soon items.
// - My team: the RDs the viewer directly supervises, whenever that list is
//   non-empty, whatever permission reaches them. A viewer whose roster IS their
//   team (supervised reach without `rds:read:all`) also gets the Team row,
//   their roster, so their landing stays one click away even before the list
//   loads or while it is empty.
// - RDs: `rds:read:all`. All RDs, the admin's Appointments entry when they have
//   no rows of their own, and Recently viewed.
// - Admin settings: an item in the account menu at the rail's foot, for
//   either admin tier (`hasAdminSettings`). The rail itself has no Admin row.
//
// Upload and Schedules are deliberately absent. Pure and relative-import only,
// so `tsx --test` runs it.

export interface RailRd {
  id: string;
  slug: string;
  name: string;
}

export type RailLinkIcon = "team" | "all-rds" | "patients" | "messages";

export type RailRow =
  | { kind: "appointments"; key: string }
  | { kind: "notifications"; key: string }
  | {
      kind: "soon";
      key: string;
      label: string;
      description: string;
      icon: "patients" | "chat";
    }
  | {
      kind: "link";
      key: string;
      label: string;
      to: "/rds" | "/patients" | "/messages";
      icon: RailLinkIcon;
    }
  | { kind: "heading"; key: string; label: string }
  | { kind: "rd"; key: string; rd: RailRd };

export interface RailSection {
  key: "practice" | "team" | "rds";
  label: string;
  rows: RailRow[];
}

export interface RailModel {
  sections: RailSection[];
}

export interface RailContext {
  rdHomeEnabled: boolean;
  /** The `notifications` flag; adds a row inside My practice, never a section. */
  notificationsEnabled: boolean;
  /** The `patient-detail` flag; swaps the Soon Patients item for the live list. */
  patientsEnabled: boolean;
  /** The `patient-messaging` flag (NO-925); adds the Messages row under Notifications. */
  messagesEnabled: boolean;
  /** The RDs whose supervisor is the viewer. */
  team: readonly RailRd[];
  /** Recently opened RDs, already resolved against what the viewer reaches. */
  recent: readonly RailRd[];
}

const APPOINTMENTS_ROW: RailRow = {
  kind: "appointments",
  key: "practice:appointments",
};
const NOTIFICATIONS_ROW: RailRow = {
  kind: "notifications",
  key: "practice:notifications",
};
export const PATIENTS_PATH = "/patients";
export const MESSAGES_PATH = "/messages";

const ADMIN_MESSAGES_ROW: RailRow = {
  kind: "link",
  key: "rds:messages",
  label: "Messages",
  to: "/messages",
  icon: "messages",
};

const MESSAGES_ROW: RailRow = {
  kind: "link",
  key: "practice:messages",
  label: "Messages",
  to: MESSAGES_PATH,
  icon: "messages",
};

const PATIENTS_ROW: RailRow = {
  kind: "link",
  key: "practice:patients",
  label: "Patients",
  to: PATIENTS_PATH,
  icon: "patients",
};
const SOON_PATIENTS_ROW: RailRow = {
  kind: "soon",
  key: "practice:patients",
  label: "Patients",
  description: "Everyone you see — history, notes, and metrics.",
  icon: "patients",
};
const SOON_CHAT_ROW: RailRow = {
  kind: "soon",
  key: "practice:chat",
  label: "Chat",
  description: "Ask about your patients and sessions.",
  icon: "chat",
};

export function hasPracticeSection(
  user: AlwaysAiUser,
  rdHomeEnabled: boolean
): boolean {
  return rdHomeEnabled && can(user, "appointments:read:own");
}

/** Supervised reach only: the viewer's /rds roster is exactly their team. */
export function rosterIsTeam(user: AlwaysAiUser): boolean {
  return canReadRdWorkspace(user) && !can(user, "rds:read:all");
}

/**
 * An admin with no caseload reaches Appointments only by choosing an RD, so the
 * rail names the surface under RDs (Justin, 2026-10-15). Rides rd-home like
 * every other Appointment entry.
 */
export function hasAdminAppointmentsEntry(
  user: AlwaysAiUser,
  rdHomeEnabled: boolean
): boolean {
  const selectsOnly =
    can(user, "appointments:read:all") && !can(user, "appointments:read:own");

  return rdHomeEnabled && selectsOnly;
}

function practiceRows(context: RailContext): RailRow[] {
  return [
    APPOINTMENTS_ROW,
    ...(context.notificationsEnabled ? [NOTIFICATIONS_ROW] : []),
    ...(context.messagesEnabled ? [MESSAGES_ROW] : []),
    context.patientsEnabled ? PATIENTS_ROW : SOON_PATIENTS_ROW,
    SOON_CHAT_ROW,
  ];
}

function rdRows(prefix: string, rds: readonly RailRd[]): RailRow[] {
  return rds.map((rd) => ({ kind: "rd", key: `${prefix}:${rd.id}`, rd }));
}

function teamSection(user: AlwaysAiUser, team: readonly RailRd[]): RailSection | null {
  const teamRow: RailRow[] = rosterIsTeam(user)
    ? [{ kind: "link", key: "team:roster", label: "Team", to: "/rds", icon: "team" }]
    : [];
  const rows = [...teamRow, ...rdRows("team", team)];

  return rows.length > 0 ? { key: "team", label: "My team", rows } : null;
}

function rdsSection(user: AlwaysAiUser, context: RailContext): RailSection | null {
  if (!can(user, "rds:read:all")) return null;

  const rows: RailRow[] = [
    { kind: "link", key: "rds:all", label: "All RDs", to: "/rds", icon: "all-rds" },
  ];

  if (hasAdminAppointmentsEntry(user, context.rdHomeEnabled)) {
    rows.push({ kind: "appointments", key: "rds:appointments" });

    // NO-925: an admin with no caseload has no My practice section, so the
    // Messages page (where they pick an RD) is reached from here, like
    // Appointments.
    if (context.messagesEnabled) rows.push(ADMIN_MESSAGES_ROW);
  }

  if (context.recent.length > 0) {
    rows.push({ kind: "heading", key: "rds:recent", label: "Recently viewed" });
    rows.push(...rdRows("recent", context.recent));
  }

  return { key: "rds", label: "RDs", rows };
}

export function railSections(user: AlwaysAiUser, context: RailContext): RailModel {
  const practice: RailSection | null = hasPracticeSection(user, context.rdHomeEnabled)
    ? {
        key: "practice",
        label: "My practice",
        rows: practiceRows(context),
      }
    : null;
  const sections = [
    practice,
    teamSection(user, context.team),
    rdsSection(user, context),
  ];

  return {
    sections: sections.filter((section): section is RailSection => section !== null),
  };
}

/**
 * Does the account menu offer Admin settings? Either admin tier, the gate the
 * rail's old Admin footer row used. The item links straight to /admin/rds:
 * the /admin index is a <Navigate> that pushes, so landing there would leave
 * a hop that traps the browser back button.
 */
export function hasAdminSettings(user: AlwaysAiUser): boolean {
  return isAlwaysAiAdmin(user);
}

/**
 * Does this viewer get a rail at all? Asked before any team or recent data
 * exists, so it reads permissions only. A VA, or an RD without rd-home, has
 * nothing to put in it and keeps the legacy chrome.
 */
export function hasRailSections(user: AlwaysAiUser, rdHomeEnabled: boolean): boolean {
  const model = railSections(user, {
    rdHomeEnabled,
    notificationsEnabled: false,
    patientsEnabled: false,
    messagesEnabled: false,
    team: [],
    recent: [],
  });

  return model.sections.length > 0 || hasAdminSettings(user);
}

/** A URL path segment, decoded; null for a malformed escape such as `%zz`. */
export function decodePathSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** The RD segment of `/rds/:rd` and everything under it, decoded. */
export function rdSegmentOf(pathname: string): string | null {
  const match = /^\/rds\/([^/]+)/.exec(pathname);

  return match ? decodePathSegment(match[1]) : null;
}

export const NOTIFICATIONS_PATH = "/notifications";

/** The Patients list and every Client page under it. */
export function isPatientsPath(pathname: string): boolean {
  return pathname === PATIENTS_PATH || pathname.startsWith(`${PATIENTS_PATH}/`);
}

/** The Messages list and every Client thread under it. */
export function isMessagesPath(pathname: string): boolean {
  return pathname === MESSAGES_PATH || pathname.startsWith(`${MESSAGES_PATH}/`);
}

function linkMatches(to: string, pathname: string): boolean {
  if (to === PATIENTS_PATH) return isPatientsPath(pathname);

  return to === MESSAGES_PATH ? isMessagesPath(pathname) : pathname === to;
}

function rowMatches(row: RailRow, pathname: string, rdSegment: string | null): boolean {
  if (row.kind === "link") return linkMatches(row.to, pathname);

  if (row.kind === "notifications") return pathname === NOTIFICATIONS_PATH;

  if (row.kind !== "rd" || rdSegment === null) return false;

  return row.rd.id === rdSegment || row.rd.slug === rdSegment;
}

/**
 * The one row that marks the current page, or null. RD rows match by slug or
 * id; the first match wins, so an RD on the team and in Recently viewed is
 * marked once. Appointment entries mark themselves.
 */
export function activeRailRow(pathname: string, model: RailModel): string | null {
  const rdSegment = rdSegmentOf(pathname);
  const rows = model.sections.flatMap((section) => section.rows);

  return rows.find((row) => rowMatches(row, pathname, rdSegment))?.key ?? null;
}

/**
 * The working surfaces where the rail folds to its icon strip on arrival: an
 * open Appointment and a SOAP Draft.
 */
export function foldsRail(pathname: string): boolean {
  return pathname.startsWith("/appointments/") || isSessionDraftPath(pathname);
}

/**
 * The pages that show a crumb trail in the rail frame. The rail names every
 * top-level page and the RD page frame's header and tabs name every RD surface,
 * so only a Session, which opens outside that frame, needs a way back up.
 * Decided by URL, not by when the crumbs load, so the page never shifts.
 */
export function showsCrumbTrail(pathname: string): boolean {
  return /^\/rds\/[^/]+\/sessions\/[^/]+$/.test(pathname);
}

const ROLE_LABELS: Record<AlwaysAiUser["role"], string> = {
  rd: "RD",
  va: "VA",
  supervisor: "Supervisor",
  clinical_admin: "Clinical admin",
  admin: "Admin",
};

/**
 * The account row's second line, under the viewer's name: the role, nothing
 * more (Justin, 2026-10-15).
 */
export function whoRoleLine(user: AlwaysAiUser): string {
  return ROLE_LABELS[user.role];
}
